// The Report control, shared by the feed, event chat and photos.
//
// App Store Review Guideline 1.2 wants a user-generated-content app to give
// users a way to report offensive content. This is that way. See
// `docs/appstore-metadata.md` for the full reading of 1.2 and what is still
// missing (block-a-user, deliberately not v1).
//
// ONE control, built once and used in three places, because three hand-rolled
// report dialogs would drift and only one of them would ever get fixed.
//
// It is not rendered on your OWN content. Reporting yourself is not a real
// action, and an app that offers it looks unfinished.

import { el } from '../dom.js';
import { ApiClientError } from '../api/client.js';
import { blockUser, reportContent } from '../api/client.js';
import {
  REPORT_REASONS,
  REPORT_REASON_LABELS,
  REPORT_NOTE_MAX,
  type ReportReason,
  type ReportTarget,
} from '@msc/shared';
import { modal, type ModalHandle } from './modal.js';

/**
 * A "Report" button for one piece of content.
 *
 * @param mine Pass the item's own `mine` flag. Returns null when true, so a
 *   caller can append the result unconditionally.
 */
export function reportButton(
  target: ReportTarget,
  targetId: number,
  mine: boolean,
): HTMLButtonElement | null {
  // Reporting your own post, or your own profile, is meaningless -- and the
  // server refuses a self-report outright, so offering the button would only
  // produce an error the user cannot act on.
  if (mine) return null;

  const btn = el(
    'button',
    { class: 'btn btn-link btn-report', type: 'button' },
    target === 'user' ? 'Report this member' : 'Report',
  ) as HTMLButtonElement;

  btn.addEventListener('click', () => openReportDialog(target, targetId));
  return btn;
}

function openReportDialog(target: ReportTarget, targetId: number): void {
  // `method="post"` even though script submits it: if the script ever fails the
  // browser must not fall back to GET. Hard rule, see CLAUDE.md.
  const form = el('form', { method: 'post', class: 'report-form' }) as HTMLFormElement;

  const fieldset = el('fieldset', { class: 'report-reasons' }, [
    el('legend', {}, 'Why are you reporting this?'),
  ]);

  const radios: HTMLInputElement[] = [];
  for (const reason of REPORT_REASONS) {
    const input = el('input', {
      type: 'radio',
      name: 'reason',
      value: reason,
    }) as HTMLInputElement;
    if (reason === REPORT_REASONS[0]) input.checked = true;
    radios.push(input);
    // `check` is the existing row pattern and is what stops the radio inheriting
    // the global `input { width: 100% }` — without it the radio stretches to the
    // full row and squeezes the label into a three-line sliver outside the
    // dialog. `report-reason` adds only the margin reset on top of it, because
    // the global `label` rule bolts 21.6px onto anything built on a <label>.
    fieldset.append(
      el('label', { class: 'check report-reason' }, [input, ' ', REPORT_REASON_LABELS[reason]]),
    );
  }

  const note = el('textarea', {
    name: 'note',
    rows: '3',
    maxlength: String(REPORT_NOTE_MAX),
    placeholder: 'Anything else we should know? (optional)',
  }) as HTMLTextAreaElement;

  const status = el('p', { class: 'muted small', role: 'status' }, '');
  const submit = el('button', { class: 'btn btn-primary', type: 'submit' }, 'Send report');
  const cancel = el('button', { class: 'btn', type: 'button' }, 'Cancel');

  form.append(
    fieldset,
    el('label', { class: 'report-note-label' }, 'Details'),
    note,
    status,
    el('div', { class: 'form-actions' }, [submit, cancel]),
  );

  // Null it BEFORE close() runs, so onClose's re-entry is a no-op. And always
  // close() before destroy(): destroy alone leaves body.modal-open set and the
  // page scroll-locked with nothing on screen. See CLAUDE.md.
  // 'Report content' would be wrong for a member: nothing they posted is being
  // reported, and a person reading it wonders which content they mean.
  let handle: ModalHandle | null = modal({
    title: target === 'user' ? 'Report this member' : 'Report content',
    body: form,
  });

  function dismiss(): void {
    const h = handle;
    handle = null;
    h?.close();
    h?.destroy();
  }

  cancel.addEventListener('click', dismiss);

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const chosen = radios.find((r) => r.checked);
    if (!chosen) return;

    submit.setAttribute('disabled', 'disabled');
    status.textContent = 'Sending…';
    try {
      const res = await reportContent({
        target,
        targetId,
        reason: chosen.value as ReportReason,
        note: note.value.trim() || undefined,
      });
      dismiss();
      window.alert(res.message);
    } catch (err) {
      status.textContent =
        err instanceof ApiClientError ? err.message : 'Could not send that report.';
      submit.removeAttribute('disabled');
    }
  });

  handle.open();
  // modal() focuses its own × — the close button sits earlier in the dialog and
  // wins the querySelector. Fixed per-form, as the quick-add does.
  radios[0]?.focus();
}

/**
 * A "Block" button beside Report, on any member's content.
 *
 * Added 2026-09-25: App Review rejected build 4 under Guideline 1.2 because a
 * block must remove the person's content "from the user's feed instantly", and
 * blocking lived only on the profile page. The server email to the operator is
 * the other half of that requirement (`server/src/blocks/routes.ts`).
 *
 * Returns null on your own content, like reportButton().
 */
export function blockButton(
  userId: number,
  displayName: string,
  mine: boolean,
): HTMLButtonElement | null {
  if (mine) return null;
  const btn = el('button', { class: 'btn btn-link btn-report', type: 'button' }, 'Block') as HTMLButtonElement;
  btn.addEventListener('click', async () => {
    const ok = window.confirm(
      `Block ${displayName}? You will no longer see their posts, messages or photos, ` +
        'and the MySocialCal team will be notified. You can unblock them from your account page.',
    );
    if (!ok) return;
    btn.setAttribute('disabled', 'disabled');
    try {
      await blockUser(userId);
      removeAuthoredBy(userId);
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Could not block that member.');
      btn.removeAttribute('disabled');
    }
  });
  return btn;
}

/**
 * Take every piece of one member's content off the current page.
 *
 * Driven by `data-author` on the rendered item. ⚠ Event announcement cards are
 * deliberately NOT tagged: a block hides a person's speech, never the group's
 * schedule (CLAUDE.md), and the server's feed filter makes the same exception.
 * A reload shows the same result, because the server already filters.
 */
export function removeAuthoredBy(userId: number): void {
  for (const node of document.querySelectorAll(`[data-author="${userId}"]`)) node.remove();
}
